/**
 * React Query Hooks for Mentors API
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { mentorsApi } from '../../api/mentors';
import { queryKeys } from '../../lib/queryClient';

/**
 * Fetch mentors for a cohort
 */
export function useMentors(cohortId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.mentors.list(cohortId!),
    queryFn: () => mentorsApi.listMentors(cohortId!),
    enabled: !!cohortId,
  });
}

/**
 * Fetch mentor requests for a cohort
 */
export function useMentorRequests(cohortId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.mentors.requests(cohortId!),
    queryFn: () => mentorsApi.listMentorRequests(cohortId!),
    enabled: !!cohortId,
  });
}

/**
 * Fetch the calling user's assigned mentor for a cohort.
 * Uses the authenticated session — no userId parameter needed.
 */
export function useMyMentor(cohortId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.mentors.mine(cohortId!),
    queryFn: () => mentorsApi.getMentorForUser(cohortId!),
    enabled: !!cohortId,
  });
}

/**
 * Assign mentor to mentee
 */
export function useAssignMentor() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ cohortId, mentorId, menteeId }: { cohortId: string; mentorId: string; menteeId: string }) =>
      mentorsApi.assignMentor(cohortId, mentorId, menteeId),
    onSuccess: (_, variables) => {
      // Invalidate mentors list and user's mentor
      queryClient.invalidateQueries({ queryKey: queryKeys.mentors.list(variables.cohortId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.mentors.forUser(variables.menteeId, variables.cohortId) });
    },
  });
}

/**
 * Remove mentor assignment
 */
export function useRemoveMentor() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ cohortId, mentorId, menteeId }: { cohortId: string; mentorId: string; menteeId: string }) =>
      mentorsApi.removeMentor(cohortId, mentorId, menteeId),
    onSuccess: (_, variables) => {
      // Invalidate mentors list and user's mentor
      queryClient.invalidateQueries({ queryKey: queryKeys.mentors.list(variables.cohortId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.mentors.forUser(variables.menteeId, variables.cohortId) });
    },
  });
}

/**
 * Request to become a mentor
 */
export function useRequestMentor() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ cohortId, userId, notes }: { cohortId: string; userId: string; notes?: string }) =>
      mentorsApi.requestMentor({ cohortId, userId, notes }),
    onSuccess: (_, variables) => {
      // Invalidate mentor requests
      queryClient.invalidateQueries({ queryKey: queryKeys.mentors.requests(variables.cohortId) });
    },
  });
}

/**
 * Approve mentor request
 */
export function useApproveMentorRequest() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ requestId }: { requestId: string; cohortId: string }) =>
      mentorsApi.approveMentorRequest(requestId),
    onSuccess: (_, variables) => {
      // Invalidate mentor requests and mentors list
      queryClient.invalidateQueries({ queryKey: queryKeys.mentors.requests(variables.cohortId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.mentors.list(variables.cohortId) });
    },
  });
}

/**
 * Auto-assign unassigned participants to mentors using even distribution
 */
export function useAutoAssignMentors() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (cohortId: string) => mentorsApi.autoAssignMentors(cohortId),
    onSuccess: (_, cohortId) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.mentors.list(cohortId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.cohorts.participants(cohortId) });
    },
  });
}

/**
 * Reject mentor request
 */
export function useRejectMentorRequest() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ requestId }: { requestId: string; cohortId: string }) =>
      mentorsApi.rejectMentorRequest(requestId),
    onSuccess: (_, variables) => {
      // Invalidate mentor requests
      queryClient.invalidateQueries({ queryKey: queryKeys.mentors.requests(variables.cohortId) });
    },
  });
}

/**
 * Directly add a user as a mentor (admin action)
 */
export function useAddMentor() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ cohortId, userId }: { cohortId: string; userId: string }) =>
      mentorsApi.addMentor(cohortId, userId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.mentors.list(variables.cohortId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.mentors.requests(variables.cohortId) });
    },
  });
}
